import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Clock, ExternalLink, RefreshCcw, XCircle, Loader2 } from 'lucide-react';
import { API } from '../utils/apiBase';
import CancellationModal from './CancellationModal';

// Subscription management banner shown on the Dashboard for any user with
// an active paid subscription. Shows state at a glance + gives one-click
// access to cancel/resume/change-plan without leaving the dashboard.

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch {
    return null;
  }
};

const ManageSubscriptionBanner = ({ subscription, onChange }) => {
  const [showCancel, setShowCancel] = useState(false);
  const [resuming, setResuming] = useState(false);

  const resume = useCallback(async () => {
    setResuming(true);
    try {
      await axios.post(`${API}/payments/resume`, {}, { withCredentials: true });
      onChange?.();
    } catch (err) {
      // Silent — banner will refresh on next fetch.
    } finally {
      setResuming(false);
    }
  }, [onChange]);

  if (!subscription) return null;
  const tier = (subscription.tier || 'free').toLowerCase();
  if (tier === 'free') return null;

  const {
    billing_period: billingPeriod,
    in_trial: inTrial,
    trial_end: trialEnd,
    current_period_end: periodEnd,
    cancel_at_period_end: cancelScheduled,
  } = subscription;

  // Choose the right visual state
  let icon, accent, title, body;

  if (cancelScheduled) {
    icon = <XCircle size={20} className="text-[#e11d48]" />;
    accent = 'border-l-4 border-[#e11d48] bg-[#fef2f2]';
    title = 'Subscription ending soon';
    body = (
      <>Your {tier} access ends on <strong>{fmtDate(periodEnd) || 'your billing date'}</strong>. All your data stays safe on the Free plan.</>
    );
  } else if (inTrial) {
    icon = <Clock size={20} className="text-[#002fa7]" />;
    accent = 'border-l-4 border-[#002fa7] bg-[#eff6ff]';
    title = `You're on a ${tier} free trial`;
    body = (
      <>Full access through <strong>{fmtDate(trialEnd) || 'day 14'}</strong>. After that, your {billingPeriod || 'yearly'} billing starts automatically.</>
    );
  } else {
    icon = <CheckCircle size={20} className="text-[#10b981]" />;
    accent = 'border-l-4 border-[#10b981] bg-[#ecfdf5]';
    title = `Active ${tier} subscription`;
    body = (
      <>Billed {billingPeriod || 'monthly'}. Next renewal: <strong>{fmtDate(periodEnd) || 'scheduled'}</strong>.</>
    );
  }

  return (
    <>
      <div
        className={`mb-6 p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${accent}`}
        data-testid="manage-subscription-banner"
      >
        <div className="flex items-start gap-3 flex-1">
          <div className="shrink-0 mt-0.5">{icon}</div>
          <div>
            <p className="font-semibold text-[#0f1115] text-sm">{title}</p>
            <p className="text-[#4b5563] text-xs mt-0.5">{body}</p>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Link
            to="/pricing"
            className="px-3 py-1.5 text-xs font-semibold border border-[#e5e7eb] bg-white text-[#4b5563] hover:text-[#0f1115] transition-colors inline-flex items-center gap-1"
            data-testid="manage-sub-change-plan"
          >
            Change plan <ExternalLink size={12} />
          </Link>
          {cancelScheduled ? (
            <button
              type="button"
              onClick={resume}
              disabled={resuming}
              className="px-3 py-1.5 text-xs font-semibold bg-[#002fa7] text-white hover:bg-[#001f70] transition-colors inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="manage-sub-resume"
            >
              {resuming ? <Loader2 size={12} className="animate-spin" /> : <RefreshCcw size={12} />}
              Resume
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setShowCancel(true)}
              className="px-3 py-1.5 text-xs font-semibold text-[#6b7280] hover:text-[#e11d48] transition-colors"
              data-testid="manage-sub-cancel"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <CancellationModal
        open={showCancel}
        subscription={subscription}
        onClose={() => setShowCancel(false)}
        onCancelled={() => onChange?.()}
      />
    </>
  );
};

export default ManageSubscriptionBanner;
